// Proposal Composer + View Date sheets (Feb 26 2026).
//
// Two additional bottom sheets that sit alongside LifecycleSheet for the
// Planned milestone:
//   • <ProposalComposer /> — Sender picks date/time/location, submits to
//     `POST /matches/{id}/date-proposals` or `.../counter` (if `mode==='counter'`).
//   • <ViewDate />        — Read-only view of the current confirmed date.
//
// Both are dumb about the API; parent (Chat.jsx) provides the callbacks
// and a fresh `lifecycle` payload so the sheet always reflects server truth.

import { useState } from 'react';
import { X, Calendar, MapPin, Loader2 } from 'lucide-react';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';


function _defaultDateTime(iso) {
  // Reasonable default: tomorrow 7pm local.
  if (iso) {
    try { return new Date(iso); } catch { /* fall through */ }
  }
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(19, 0, 0, 0);
  return d;
}


function _toDatetimeLocalValue(d) {
  // <input type="datetime-local"> wants YYYY-MM-DDTHH:mm (no seconds, no TZ).
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}


export function ProposalComposer({ open, onClose, matchId, otherName, mode = 'propose',
                                    initialProposal = null, onSubmitted }) {
  const initialDate = _defaultDateTime(initialProposal?.date_at);
  const [dateLocal, setDateLocal] = useState(() => _toDatetimeLocalValue(initialDate));
  const [location, setLocation] = useState(initialProposal?.location || '');
  const [notes, setNotes] = useState(initialProposal?.notes || '');
  const [submitting, setSubmitting] = useState(false);
  if (!open) return null;

  const submit = async () => {
    if (!location.trim()) {
      toast.error('Add a place or venue');
      return;
    }
    // Convert local datetime to UTC ISO.
    const localDate = new Date(dateLocal);
    if (isNaN(localDate.getTime())) {
      toast.error('Pick a valid date and time');
      return;
    }
    if (localDate.getTime() - Date.now() < 60_000) {
      toast.error('Pick a time in the future');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        date_at: localDate.toISOString(),
        location: location.trim(),
        notes: notes.trim() || null,
      };
      let r;
      if (mode === 'counter' && initialProposal) {
        r = await api.post(`/date-proposals/${initialProposal.proposal_id}/counter`, payload);
      } else {
        r = await api.post(`/matches/${matchId}/date-proposals`, payload);
      }
      toast.success(mode === 'counter' ? 'Counter-proposal sent' : 'Proposal sent!');
      onSubmitted?.(r.data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Could not send proposal');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
         onClick={onClose} data-testid="proposal-composer">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 pb-8 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#B38138]">
              {mode === 'counter' ? '↩️ Counter-proposal' : '📅 Propose a date'}
            </p>
            <h3 className="font-brand text-[20px] font-[500] tracking-tight text-[#0F0F10] mt-0.5">
              {mode === 'counter' ? `Suggest a different time to ${otherName}` : `Plan a date with ${otherName}`}
            </h3>
          </div>
          <button onClick={onClose} data-testid="proposal-composer-close"
                  className="p-1 rounded-full hover:bg-black/5">
            <X size={18} strokeWidth={2} className="text-[#4B4B52]" />
          </button>
        </div>

        <label className="block text-[12px] font-semibold text-[#0F0F10] mb-1.5 mt-3">
          <Calendar size={12} className="inline mr-1" /> Date &amp; time
        </label>
        <input
          type="datetime-local"
          value={dateLocal}
          onChange={(e) => setDateLocal(e.target.value)}
          data-testid="proposal-date-input"
          className="w-full px-3 py-2.5 rounded-xl border border-[var(--hairline-strong)] text-[14px] focus:outline-none focus:border-[#B38138]"
        />

        <label className="block text-[12px] font-semibold text-[#0F0F10] mb-1.5 mt-4">
          <MapPin size={12} className="inline mr-1" /> Place / venue
        </label>
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value.slice(0, 200))}
          placeholder="Blue Bottle · Ferry Building"
          data-testid="proposal-location-input"
          className="w-full px-3 py-2.5 rounded-xl border border-[var(--hairline-strong)] text-[14px] focus:outline-none focus:border-[#B38138]"
        />

        <label className="block text-[12px] font-semibold text-[#0F0F10] mb-1.5 mt-4">
          Notes (optional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value.slice(0, 500))}
          placeholder="I know you love their oat matcha…"
          rows={3}
          data-testid="proposal-notes-input"
          className="w-full px-3 py-2.5 rounded-xl border border-[var(--hairline-strong)] text-[14px] focus:outline-none focus:border-[#B38138] resize-none"
        />

        <button
          onClick={submit}
          disabled={submitting || !location.trim()}
          data-testid="proposal-submit-btn"
          className="w-full mt-5 py-3.5 rounded-full text-white font-semibold text-[14px] tracking-tight transition-all hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: '#B38138' }}
        >
          {submitting ? <Loader2 size={16} className="inline animate-spin" /> : (mode === 'counter' ? 'Send counter-proposal' : 'Send proposal')}
        </button>
      </div>
    </div>
  );
}


export function ViewDate({ open, onClose, proposal, otherName }) {
  if (!open || !proposal) return null;
  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined,
        { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch {
      return iso;
    }
  };
  return (
    <div className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
         onClick={onClose} data-testid="view-date-sheet">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 pb-8 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#15803D]">🎉 Planned</p>
            <h3 className="font-brand text-[20px] font-[500] tracking-tight text-[#0F0F10] mt-0.5">
              Your date with {otherName}
            </h3>
          </div>
          <button onClick={onClose} data-testid="view-date-close"
                  className="p-1 rounded-full hover:bg-black/5">
            <X size={18} strokeWidth={2} className="text-[#4B4B52]" />
          </button>
        </div>

        <div className="rounded-2xl border border-[var(--hairline-strong)] p-4 space-y-3">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-[#8A8A93] font-semibold">When</p>
            <p className="text-[15px] text-[#0F0F10] font-medium mt-0.5">{fmt(proposal.date_at)}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-[#8A8A93] font-semibold">Where</p>
            <p className="text-[15px] text-[#0F0F10] font-medium mt-0.5">{proposal.location}</p>
          </div>
          {proposal.notes && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#8A8A93] font-semibold">Notes</p>
              <p className="text-[14px] text-[#4B4B52] mt-0.5 leading-relaxed">{proposal.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
